import React, { useState } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { FiX, FiCalendar, FiSave, FiClock } from 'react-icons/fi';
import apiService from '../../services/api';
import { 
  getChicagoDateString,
  getChicagoTimezoneAbbr
} from '../../utils/datetime-comprehensive';

const ModalOverlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: var(--background-overlay);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1rem;
`;

const ModalContent = styled(motion.div)`
  background-color: var(--background-primary);
  border-radius: 0;
  padding: 2rem;
  width: 100%;
  max-width: 600px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
`;

const ModalTitle = styled.h2`
  color: var(--text-primary);
  font-family: 'Inter', sans-serif;
  font-size: 1.5rem;
  font-weight: 500;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: var(--text-muted);
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 0;
  transition: all 0.2s ease;
  
  &:hover {
    color: var(--text-primary);
    background-color: var(--background-secondary);
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const Label = styled.label`
  color: var(--text-primary);
  font-weight: 500;
  font-size: 0.875rem;
`;

const Input = styled.input`
  padding: 0.75rem;
  border: 1px solid var(--border-color);
  border-radius: 0;
  background-color: var(--background-primary);
  color: var(--text-primary);
  font-size: 0.875rem;
  font-family: 'Inter', sans-serif;
  transition: all 0.2s ease;
  
  &:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 3px rgba(216, 184, 137, 0.1);
  }
`;

const Textarea = styled.textarea`
  padding: 0.75rem;
  border: 1px solid var(--border-color);
  border-radius: 0;
  background-color: var(--background-primary);
  color: var(--text-primary);
  font-size: 0.875rem;
  font-family: 'Inter', sans-serif;
  transition: all 0.2s ease;
  resize: vertical;
  min-height: 80px;
  
  &:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 3px rgba(216, 184, 137, 0.1);
  }
`;

const CourseInfo = styled.div`
  background-color: var(--background-secondary);
  border: 1px solid var(--border-color);
  border-radius: 0;
  padding: 1rem;
  margin-bottom: 1rem;
`;

const CourseTitle = styled.h3`
  font-size: 1.125rem;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0 0 0.5rem 0;
`;

const CourseCode = styled.div`
  font-size: 0.875rem;
  color: var(--text-secondary);
  font-weight: 500;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 1rem;
`;

const Button = styled.button`
  flex: 1;
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 0;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  min-height: 44px;
  
  &.primary {
    background-color: var(--primary-color);
    color: var(--text-inverse);
    
    &:hover:not(:disabled) {
      background-color: var(--primary-dark);
      transform: translateY(-1px);
      box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
    }
    
    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
      transform: none;
      box-shadow: none;
    }
  }
  
  &.secondary {
    background-color: var(--background-tertiary);
    color: var(--text-primary);
    border: 1px solid var(--border-color);
    
    &:hover {
      background-color: var(--background-secondary);
      border-color: var(--border-dark);
      transform: translateY(-1px);
      box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    }
  }
`;

const LoadingSpinner = styled.div`
  width: 16px;
  height: 16px;
  border: 2px solid transparent;
  border-top: 2px solid currentColor;
  border-radius: 0;
  animation: spin 1s linear infinite;
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const ErrorMessage = styled(motion.div)`
  background: rgba(252, 129, 129, 0.1);
  border: 1px solid var(--error-color);
  color: var(--error-color);
  padding: 0.75rem;
  border-radius: 0;
  font-size: 0.875rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;


const TimeRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  margin-bottom: 1rem;
`;


const HelpText = styled.div`
  font-size: 0.75rem;
  color: var(--text-secondary);
  margin-top: 0.5rem;
  line-height: 1.4;
`;

const TimezoneIndicator = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem;
  background-color: var(--background-secondary);
  border: 1px solid var(--border-color);
  border-radius: 0;
  margin-bottom: 1rem;
  font-size: 0.875rem;
  color: var(--text-primary);
  
  svg {
    color: var(--primary-color);
  }
`;

const TimezoneText = styled.span`
  font-weight: 500;
`;

// Build a YYYY-MM-DDTHH:MM:00 string (local time, no timezone suffix) from the
// explicit date / hour / minute / AM-PM inputs. Returns '' if any part is
// missing or invalid. The backend reinterprets this string in Chicago tz.
const composeScheduledAt = (dateStr, hour, minute, meridiem) => {
  if (!dateStr || !hour || minute === '' || minute === undefined || !meridiem) return '';
  const h = parseInt(hour, 10);
  const m = parseInt(minute, 10);
  if (Number.isNaN(h) || h < 1 || h > 12) return '';
  if (Number.isNaN(m) || m < 0 || m > 59) return '';
  const hour24 = meridiem === 'PM'
    ? (h === 12 ? 12 : h + 12)
    : (h === 12 ? 0 : h);
  const hh = String(hour24).padStart(2, '0');
  const mm = String(m).padStart(2, '0');
  return `${dateStr}T${hh}:${mm}:00`;
};

function CreateAttendanceModal({ isOpen, onClose, course, elective, onSessionCreated }) {
  const [formData, setFormData] = useState({
    date: getChicagoDateString(),
    startTime: '19:00',
    endTime: '21:00',
    title: '',
    notes: '',
    scheduleForLater: false,
    scheduledDate: '',
    scheduledHour: '',
    scheduledMinute: '',
    scheduledMeridiem: 'AM',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Determine if we're working with a course or elective
  const entity = course || elective;

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  // Hour input: 1–12 only, strips non-digits, hard-clamps to 12. Empty allowed
  // so the user can backspace and retype.
  const handleScheduledHourChange = (e) => {
    let value = e.target.value.replace(/[^0-9]/g, '').slice(0, 2);
    if (value !== '' && parseInt(value, 10) > 12) value = '12';
    setFormData(prev => ({ ...prev, scheduledHour: value }));
  };

  // Minute input: 0–59 only, strips non-digits, hard-clamps to 59. Empty allowed.
  const handleScheduledMinuteChange = (e) => {
    let value = e.target.value.replace(/[^0-9]/g, '').slice(0, 2);
    if (value !== '' && parseInt(value, 10) > 59) value = '59';
    setFormData(prev => ({ ...prev, scheduledMinute: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.date) {
      setError('Please select a date for the attendance session');
      return;
    }

    if (!entity) {
      setError('No course or elective selected');
      return;
    }

    let composedScheduledAt = '';
    if (formData.scheduleForLater) {
      composedScheduledAt = composeScheduledAt(
        formData.scheduledDate,
        formData.scheduledHour,
        formData.scheduledMinute,
        formData.scheduledMeridiem
      );
      if (!composedScheduledAt) {
        setError('Please set a complete date, hour, minute and AM/PM for when the session should be created');
        return;
      }
      if (!formData.title || !formData.title.trim()) {
        setError('Session title is required when scheduling for later');
        return;
      }
      // Must be in the future.
      const scheduledDateTime = new Date(composedScheduledAt);
      if (Number.isNaN(scheduledDateTime.getTime()) || scheduledDateTime <= new Date()) {
        setError('Scheduled time must be in the future');
        return;
      }
    }

    try {
      setLoading(true);
      setError('');

      if (formData.scheduleForLater) {
        const payload = {
          scheduledAt: composedScheduledAt,
          sessionDate: formData.date,
          startTime: formData.startTime,
          endTime: formData.endTime,
          title: formData.title.trim(),
          notes: formData.notes.trim() || null,
        };
        if (course) payload.courseId = course.id;
        else if (elective) payload.electiveId = elective.id;

        const response = await apiService.createScheduledAttendanceSession(payload);
        if (response.success) {
          onSessionCreated({ scheduled: true, id: response.id });
          handleClose();
        } else {
          setError(response.message || 'Failed to schedule attendance session');
        }
        return;
      }

      const sessionData = {
        date: formData.date,
        startTime: formData.startTime,
        endTime: formData.endTime,
        title: formData.title.trim() || null,
        notes: formData.notes.trim() || null
      };

      // Add courseId or electiveId based on entity type
      if (course) {
        sessionData.courseId = course.id;
      } else if (elective) {
        sessionData.electiveId = elective.id;
      }

      const response = await apiService.createAttendanceSession(sessionData);

      if (response.success) {
        onSessionCreated(response.session);
        handleClose();
      } else {
        setError(response.message || 'Failed to create attendance session');
      }
    } catch (error) {
      console.error('Error creating attendance session:', error);
      setError(formData.scheduleForLater
        ? 'Failed to schedule attendance session. Please try again.'
        : 'Failed to create attendance session. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      date: getChicagoDateString(),
      startTime: '19:00',
      endTime: '21:00',
      title: '',
      notes: '',
      scheduleForLater: false,
      scheduledDate: '',
      scheduledHour: '',
      scheduledMinute: '',
      scheduledMeridiem: 'AM',
    });
    setError('');
    setLoading(false);
    onClose();
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <ModalOverlay
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={handleClose}
        >
          <ModalContent
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.95, opacity: 0 }}
            onClick={(e) => e.stopPropagation()}
          >
            <ModalHeader>
              <ModalTitle>
                <FiCalendar size={24} style={{ color: 'var(--primary-color)' }} />
                Create Attendance Session
              </ModalTitle>
              <CloseButton onClick={handleClose}>
                <FiX size={20} />
              </CloseButton>
            </ModalHeader>

            <CourseInfo>
              <CourseTitle>{entity?.name || 'N/A'}</CourseTitle>
              <CourseCode>{entity?.code || 'N/A'}</CourseCode>
            </CourseInfo>

            <TimezoneIndicator>
              <FiClock size={16} />
              <TimezoneText>All times are displayed in Chicago timezone ({getChicagoTimezoneAbbr(new Date())})</TimezoneText>
            </TimezoneIndicator>

            <Form onSubmit={handleSubmit}>
              <FormGroup>
                <Label htmlFor="date">Date *</Label>
                <Input
                  type="date"
                  id="date"
                  name="date"
                  value={formData.date}
                  onChange={handleInputChange}
                  required
                />
              </FormGroup>

              <TimeRow>
                <FormGroup>
                  <Label htmlFor="startTime">Start Time *</Label>
                  <Input
                    type="time"
                    id="startTime"
                    name="startTime"
                    value={formData.startTime}
                    onChange={handleInputChange}
                    required
                  />
                </FormGroup>
                <FormGroup>
                  <Label htmlFor="endTime">End Time *</Label>
                  <Input
                    type="time"
                    id="endTime"
                    name="endTime"
                    value={formData.endTime}
                    onChange={handleInputChange}
                    required
                  />
                </FormGroup>
              </TimeRow>
              <HelpText>
                Times are in Chicago timezone ({getChicagoTimezoneAbbr(new Date())}). 
                The system automatically handles daylight saving time changes.
              </HelpText>

              <FormGroup>
                <Label htmlFor="title">
                  Session Title {formData.scheduleForLater ? '*' : '(Optional)'}
                </Label>
                <Input
                  type="text"
                  id="title"
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
                  placeholder="e.g., Week 1 - Introduction to Aqeedah"
                  maxLength={255}
                  required={formData.scheduleForLater}
                />
              </FormGroup>

              <FormGroup>
                <Label htmlFor="notes">Notes (Optional)</Label>
                <Textarea
                  id="notes"
                  name="notes"
                  value={formData.notes}
                  onChange={handleInputChange}
                  placeholder="Any additional notes about this session..."
                  maxLength={1000}
                />
              </FormGroup>

              <FormGroup style={{ flexDirection: 'row', alignItems: 'center', gap: '0.5rem' }}>
                <input
                  type="checkbox"
                  id="scheduleForLater"
                  name="scheduleForLater"
                  checked={formData.scheduleForLater}
                  onChange={handleInputChange}
                  style={{ width: 18, height: 18, cursor: 'pointer' }}
                />
                <Label htmlFor="scheduleForLater" style={{ margin: 0, cursor: 'pointer' }}>
                  Schedule for later (create automatically at a set time)
                </Label>
              </FormGroup>

              {formData.scheduleForLater && (
                <FormGroup>
                  <Label htmlFor="scheduledDate">Create at *</Label>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 80px 80px 90px', gap: '0.5rem', alignItems: 'stretch' }}>
                    <Input
                      type="date"
                      id="scheduledDate"
                      name="scheduledDate"
                      value={formData.scheduledDate}
                      onChange={handleInputChange}
                      required
                      aria-label="Date"
                    />
                    <Input
                      type="text"
                      inputMode="numeric"
                      id="scheduledHour"
                      name="scheduledHour"
                      value={formData.scheduledHour}
                      onChange={handleScheduledHourChange}
                      placeholder="HH"
                      maxLength={2}
                      required
                      aria-label="Hour (1-12)"
                    />
                    <Input
                      type="text"
                      inputMode="numeric"
                      id="scheduledMinute"
                      name="scheduledMinute"
                      value={formData.scheduledMinute}
                      onChange={handleScheduledMinuteChange}
                      placeholder="MM"
                      maxLength={2}
                      required
                      aria-label="Minute (00-59)"
                    />
                    <Input
                      as="select"
                      id="scheduledMeridiem"
                      name="scheduledMeridiem"
                      value={formData.scheduledMeridiem}
                      onChange={handleInputChange}
                      aria-label="AM or PM"
                    >
                      <option value="AM">AM</option>
                      <option value="PM">PM</option>
                    </Input>
                  </div>
                  <HelpText>
                    The attendance session will be created automatically at this date/time
                    (Chicago timezone, {getChicagoTimezoneAbbr(new Date())}). The session itself
                    will use the date and times you set above.
                  </HelpText>
                </FormGroup>
              )}

              {error && (
                <ErrorMessage
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                >
                  {error}
                </ErrorMessage>
              )}

              <ButtonGroup>
                <Button type="button" className="secondary" onClick={handleClose}>
                  Cancel
                </Button>
                <Button 
                  type="submit" 
                  className="primary" 
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <LoadingSpinner />
                      {formData.scheduleForLater ? 'Scheduling...' : 'Creating...'}
                    </>
                  ) : (
                    <>
                      <FiSave size={16} />
                      {formData.scheduleForLater ? 'Schedule Session' : 'Create Session'}
                    </>
                  )}
                </Button>
              </ButtonGroup>
            </Form>
          </ModalContent>
        </ModalOverlay>
      )}
    </AnimatePresence>
  );
}

export default CreateAttendanceModal;
